import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthGuard from '@/components/feature/AuthGuard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';
import { supabase } from '@/lib/supabase';
import EvidenceMediaManager from './components/EvidenceMediaManager';

interface PracticeEvent {
  id: number;
  sequence_no: number;
  state: string;
  event_description: string | null;
  body_sensation: string | null;
  emotions: string[] | null;
  emotion_free_input: string | null;
  meaning: string | null;
  automatic_response: string | null;
  new_response: string | null;
  result_gratitude: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export default function Evidence() {
  return (
    <AuthGuard>
      {(auth) => <EvidenceInner user={auth.user as { id: string; email?: string } | null} />}
    </AuthGuard>
  );
}

function EvidenceInner({ user }: { user: { id: string; email?: string } | null }) {
  const [events, setEvents] = useState<PracticeEvent[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const { data, error: queryError } = await supabase
      .from('practice_events')
      .select('id,sequence_no,state,event_description,body_sensation,emotions,emotion_free_input,meaning,automatic_response,new_response,result_gratitude,confirmed_at,created_at')
      .eq('user_id', user.id)
      .eq('state', 'SUBMITTED')
      .order('confirmed_at', { ascending: false });

    if (queryError) {
      setError(`無法載入你的 Evidence 紀錄：${queryError.message}`);
      setEvents([]);
    } else {
      const loaded = (data || []) as PracticeEvent[];
      setEvents(loaded);
      setExpandedId((current) => current ?? loaded[0]?.id ?? null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />

      <header className="bg-background-50 pb-8 pt-20 text-center md:pt-24">
        <div className="mb-3 flex items-center justify-center">
          <AngelIcon variant="gold" size="md" />
        </div>
        <h1 className="mb-1 text-2xl text-foreground-800 md:text-3xl" style={{ fontFamily: 'var(--font-heading)' }}>
          Evidence｜生命實證
        </h1>
        <p className="text-xs uppercase tracking-[0.15em] text-foreground-400">Your Living Evidence</p>
        <p className="mx-auto mt-4 max-w-xl px-4 text-sm leading-relaxed text-foreground-500">
          保存你真正走過的路。圖片、聲音與影片會存放在私有空間，只有你的登入帳號能讀取。
        </p>
        {user?.email && (
          <p className="mt-3 text-xs text-foreground-400">已登入：{user.email}</p>
        )}
      </header>

      <main className="mx-auto max-w-4xl px-4 pb-20 md:px-6">
        {!user ? (
          <div className="rounded-xl border border-foreground-200 bg-background-100 p-8 text-center space-y-4">
            <p className="text-sm text-foreground-600">請先登入 AUM1001，才能查看及管理自己的 Evidence。</p>
            <Link to="/account?returnTo=%2Fevidence" className="inline-flex rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600">
              前往登入
            </Link>
          </div>
        ) : loading ? (
          <div className="py-16 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-accent-200 border-t-accent-500" />
            <p className="mt-3 text-sm text-foreground-500">正在載入你的生命實證……</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
            <p className="text-sm text-rose-700">{error}</p>
            <button type="button" onClick={loadEvents} className="mt-4 rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50">重試</button>
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-xl border border-foreground-200 bg-background-100 p-8 text-center space-y-4">
            <i className="ri-file-list-3-line text-3xl text-foreground-300" />
            <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>尚無已完成的七欄紀錄</h2>
            <p className="text-sm text-foreground-500">完成一次七欄練習後，就能在這裡補上圖片、音訊或影片 Evidence。</p>
            <Link to="/portal-practice" className="inline-flex rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600">
              前往七欄練習
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-relaxed text-emerald-800">
              已載入 {events.length} 筆屬於你的正式紀錄。點開紀錄即可新增或管理 Evidence 媒體。
            </div>

            {events.map((event) => {
              const expanded = expandedId === event.id;
              return (
                <article key={event.id} className="overflow-hidden rounded-xl border border-foreground-200/60 bg-background-100">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : event.id)}
                    className="flex w-full items-center gap-4 px-4 py-4 text-left hover:bg-foreground-50/60 md:px-5"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                      {event.sequence_no}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-foreground-400">{formatDate(event.confirmed_at || event.created_at)}</p>
                      <p className="mt-1 truncate text-sm font-medium text-foreground-800">
                        {event.event_description || '未命名紀錄'}
                      </p>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] text-emerald-700">已完成</span>
                    <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>

                  {expanded && (
                    <div className="border-t border-foreground-200/50 px-4 py-5 md:px-5">
                      <SevenFieldSummary event={event} />
                      <EvidenceMediaManager eventId={event.id} userId={user.id} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function SevenFieldSummary({ event }: { event: PracticeEvent }) {
  const emotions = [...(event.emotions || []), event.emotion_free_input || ''].filter(Boolean).join('、');
  const rows = [
    ['① 事件', event.event_description],
    ['② 身體感覺', event.body_sensation],
    ['③ 情緒', emotions],
    ['④ 理解', event.meaning],
    ['⑤ 自動反應', event.automatic_response],
    ['⑥ 新回應', event.new_response],
    ['⑦ 結果與感恩', event.result_gratitude],
  ];

  return (
    <div className="mb-5 space-y-2 rounded-lg bg-foreground-50/60 p-4">
      {rows.map(([label, value]) => (
        <p key={label} className="text-xs leading-relaxed text-foreground-600">
          <span className="font-medium text-foreground-700">{label}：</span>{value || '—'}
        </p>
      ))}
    </div>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
